import type { FactProposal, InboxAsk } from "@rakazo/contracts";
import { isRetryableError } from "@rakazo/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSpaceQueries } from "./query";

const NO_ASKS: InboxAsk[] = [];
const NO_FACTS: FactProposal[] = [];

/**
 * The current Space's inbox: pending asks and company facts to review. The inbox page, its nav
 * badge, Settings → Memory and the bot details panel all read this one query, so every count
 * matches what the inbox lists.
 */
export function useInboxAsks() {
  const queries = useSpaceQueries();
  const query = useQuery(
    queries.inbox.list.queryOptions({
      // A refused request (signed out, forbidden) stops polling.
      refetchInterval: (state) =>
        state.state.error && !isRetryableError(state.state.error) ? false : 15_000,
    }),
  );
  const asks = query.data?.asks ?? NO_ASKS;
  const facts = query.data?.facts ?? NO_FACTS;
  return { query, asks, facts, count: asks.length + facts.length };
}

/**
 * The chats with something in the inbox: the thread each ask waits in (a group chat, else the
 * bot's own chat) and the bot behind each fact proposal. The roster's Needs you filter uses it.
 */
export function inboxWaitingChatIds(
  asks: readonly Pick<InboxAsk, "botId" | "groupId">[],
  facts: readonly Pick<FactProposal, "botId">[],
): ReadonlySet<string> {
  return new Set([
    ...asks.map((ask) => ask.groupId ?? ask.botId),
    ...facts.flatMap((proposal) => (proposal.botId ? [proposal.botId] : [])),
  ]);
}

/**
 * Drops an answered ask (by message id) or a decided fact proposal (by id) from the cached
 * inbox at once, so the list and every count agree before the refetch confirms it.
 */
export function useRemoveFromInbox() {
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  return (id: string) => {
    const key = queries.inbox.list.queryKey();
    queryClient.setQueryData(key, (current) =>
      current
        ? {
            ...current,
            asks: current.asks.filter((ask) => ask.messageId !== id),
            facts: (current.facts ?? []).filter((proposal) => proposal.id !== id),
          }
        : current,
    );
    void queryClient.invalidateQueries({ queryKey: queries.inbox.list.key() });
  };
}
